import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, Trash2, Pencil, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { PasswordInput } from "../../components/PasswordInput";

const ROLES = [
  { key: "super_admin", label: "Super Admin", desc: "Full access to everything" },
  { key: "manager", label: "Manager", desc: "Orders, Customers, Menu, Analytics" },
  { key: "order_manager", label: "Order Manager", desc: "Orders & delivery status only" },
  { key: "marketing_manager", label: "Marketing / CRM", desc: "Leads, Coupons, Customers, Analytics" },
];
const EMPTY = { name: "", email: "", password: "", role: "manager" };

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editId) await api.put(`/admin/users/${editId}`, form);
      else await api.post("/admin/users", form);
      toast.success("Admin saved"); setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/users/${id}`); toast.success("Removed"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const edit = (u) => { setForm({ name: u.name, email: u.email, password: "", role: u.role }); setEditId(u.id); setOpen(true); };

  return (
    <div data-testid="admin-users">
      <div className="flex justify-between items-center mb-6">
        <div><h1 className="font-serif text-3xl text-navy">Admin Users & Roles</h1><p className="text-sm text-muted-foreground">Manage staff access with role-based permissions</p></div>
        <button onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }} data-testid="new-admin-user-btn" className="flex items-center gap-2 bg-navy text-cream text-sm font-semibold px-4 py-2.5 rounded-full hover:bg-navy-soft"><Plus size={15} /> Add Admin</button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {ROLES.map((r) => (
          <div key={r.key} className="bg-white rounded-xl border border-black/5 p-4"><div className="flex items-center gap-2 text-navy font-semibold"><ShieldCheck size={15} className="text-gold" /> {r.label}</div><p className="text-xs text-muted-foreground mt-1">{r.desc}</p></div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-black/5 overflow-x-auto">
        <table className="w-full min-w-[620px] text-sm">
          <thead className="bg-beige text-navy text-left"><tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Access</th><th className="p-3"></th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-black/5 hover:bg-beige/40" data-testid={`admin-user-${u.email}`}>
                <td className="p-3 font-medium text-navy">{u.name}{u.id === me?.id && <span className="text-xs text-gold ml-1">(you)</span>}</td>
                <td className="p-3 text-muted-foreground">{u.email}</td>
                <td className="p-3"><span className="text-xs bg-navy text-gold px-2 py-1 rounded-full capitalize">{u.role.replace(/_/g, " ")}</span></td>
                <td className="p-3 text-xs text-muted-foreground">{u.permissions.includes("*") ? "All sections" : u.permissions.join(", ")}</td>
                <td className="p-3"><div className="flex gap-2"><button onClick={() => edit(u)} className="text-muted-foreground hover:text-navy"><Pencil size={15} /></button><button onClick={() => del(u.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Edit" : "New"} Admin User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-user-name" className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" />
            <input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editId} data-testid="admin-user-email" className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm disabled:bg-beige" />
            <PasswordInput placeholder={editId ? "New password (leave blank to keep)" : "Password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} testId="admin-user-password" className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" />
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger data-testid="admin-user-role"><SelectValue /></SelectTrigger><SelectContent>{ROLES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent></Select>
            <button onClick={save} data-testid="save-admin-user-btn" className="w-full bg-navy text-cream font-semibold py-3 rounded-full hover:bg-navy-soft">Save Admin</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
